<template>
  <div class="signin-page">
    <MainNavbar />

    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />

    <section class="signin-wrapper">
      <div class="signin-card">
        <h1 class="title">Sign in to IPA Store</h1>

        <form class="signin-form" @submit.prevent="submitLogin">
          <!-- EMAIL -->
          <div class="form-group">
            <label>Email</label>

            <!-- INLINE ERROR -->
            <p v-if="errors.email" class="error-text">
              <i class="fa-solid fa-circle-exclamation"></i> {{ errors.email }}
            </p>

            <input
              type="email"
              v-model="form.email"
              placeholder="Enter your email"
              :class="{ 'input-error': errors.email }"
              required
            />
          </div>

          <!-- PASSWORD -->
          <div class="form-group">
            <label>Password</label>

            <!-- INLINE ERROR -->
            <p v-if="errors.password" class="error-text">
              <i class="fa-solid fa-circle-exclamation"></i> {{ errors.password }}
            </p>

            <input
              type="password"
              v-model="form.password"
              placeholder="Enter your password"
              :class="{ 'input-error': errors.password }"
              required
            />
          </div>

          <!-- REMEMBER ME -->
          <div class="remember-row">
            <div class="checkbox-group">
              <input id="remember" type="checkbox" v-model="form.remember" />
              <label for="remember">Remember me</label>
            </div>

            <router-link to="/ipromiseafrica/account/forgot-password" class="forgot-link">
              Forgot password?
            </router-link>
          </div>

          <!-- SUBMIT BUTTON OR SUSPENSION MESSAGE -->
          <div v-if="!accountSuspended">
            <button
              type="submit"
              class="submit-btn"
              :disabled="loading"
            >
              {{ loading ? "Sending OTP..." : "Sign In" }}
            </button>
          </div>
          
          <!-- SUSPENSION MESSAGE -->
          <div v-else class="suspension-message">
            <i class="fa-solid fa-ban"></i>
            <p>Your account has been suspended. Please contact our support team at <strong>support@ipastore.com</strong> for assistance.</p>
          </div>
        </form>

        <!-- FOOTER -->
        <p class="create-account">
          Don't have an IPA Account?
          <router-link to="/ipromiseafrica/account">Create Your IPA Account</router-link>
        </p>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Customer Login Component
 * 
 * Handles customer authentication flow for IPA Store. This component:
 * - Validates user credentials (email and password)
 * - Sends login request to the authentication API
 * - Handles various error scenarios (suspended accounts, invalid credentials, network errors)
 * - Redirects to OTP verification page upon successful login request
 * 
 * @module routes/main/CustomerLogin
 * @component CustomerSignIn
 * 
 * @example
 *   <CustomerSignIn />
 */

import MainNavbar from "../../components/main/headernfooter/header.vue";
import ToastContainer from "../../components/ui/ToastContainer.vue";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: "CustomerSignIn",
  mixins: [toastMixin],
  components: { 
    MainNavbar,
    ToastContainer
  },

  data() {
    return {
      /** @type {Object} Form data containing user credentials */
      form: {
        email: "",
        password: "",
        remember: false,
      },
      /** @type {boolean} Loading state to prevent multiple submissions */
      loading: false,
      /** @type {boolean} Flag indicating if the account is suspended */
      accountSuspended: false,
      /** @type {Object} Validation error messages */
      errors: {
        email: "",
        password: "",
      },
      /** @type {Array} Toast notifications array */
    };
  },

  methods: {
    /**
     * Resets all error states and suspension flag
     * @private
     */
    resetErrors() {
      this.errors.email = "";
      this.errors.password = "";
      this.accountSuspended = false;
    },

    /**
     * Validates form fields before submission
     * @returns {boolean} True if form is valid, false otherwise
     * @private
     */
    validateForm() {
      let isValid = true;
      const missingFields = [];

      if (!this.form.email?.trim()) {
        this.errors.email = "Email is required";
        missingFields.push("email");
        isValid = false;
      }

      if (!this.form.password) {
        this.errors.password = "Password is required";
        missingFields.push("password");
        isValid = false;
      }

      // Show toast for validation errors
      if (!isValid) {
        this.showToast(
          "warning",
          "Validation Error",
          "Please fill in all required fields before submitting."
        );
      }

      return isValid;
    },

    /**
     * Handles API error responses and sets appropriate error messages
     * @param {Object} errorData - Error response from the API
     * @private
     */
    handleApiError(errorData) {
      const errorDetail = errorData.detail?.toLowerCase() || "";

      // Check for suspended account
      if (errorDetail.includes("suspended")) {
        this.accountSuspended = true;
        this.form.password = ""; // Clear password for security
        this.showToast(
          "error",
          "Account Suspended",
          "Your account has been suspended. Please contact support@ipastore.com for assistance."
        );
        return;
      }

      // Handle credential errors
      let errorMessage = "";
      if (errorDetail.includes("email") || errorDetail.includes("not found")) {
        this.errors.email = "Incorrect email";
        errorMessage = "Invalid email address. Please check and try again.";
      }
      if (errorDetail.includes("password") || errorDetail.includes("invalid")) {
        this.errors.password = "Incorrect password";
        errorMessage = "Invalid password. Please check and try again.";
      }

      // Generic error message if no specific error was set
      if (!this.errors.email && !this.errors.password) {
        this.errors.email = "Invalid credentials. Please try again.";
        errorMessage = "Invalid credentials. Please check your email and password.";
      }

      // Show toast notification
      if (errorMessage) {
        this.showToast("error", "Login Failed", errorMessage);
      }
    },

    /**
     * Handles network errors and other fetch failures
     * @param {Error} error - The error object
     * @private
     */
    handleNetworkError(error) {
      console.error("Login Error:", error);
      this.errors.email = "Network error. Please check your connection and try again.";
      this.showToast(
        "error",
        "Network Error",
        "Unable to connect to the server. Please check your internet connection and try again."
      );
    },

    /**
     * Submits login credentials to the authentication API
     * 
     * Flow:
     * 1. Validates form inputs
     * 2. Sends POST request to /auth/login-request
     * 3. On success: Redirects to OTP verification page
     * 4. On error: Displays appropriate error messages
     * 
     * @async
     * @returns {Promise<void>}
     */
    async submitLogin() {
      // Reset previous errors
      this.resetErrors();

      // Validate form before submission
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;

      try {
        const response = await fetch(buildApiUrl("/auth/login-request"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: this.form.email.trim(),
            password: this.form.password,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            detail: "An unexpected error occurred",
          }));
          this.handleApiError(errorData);
          return;
        }

        // Success: Show success toast and redirect to OTP verification page
        // Loading state remains true to prevent multiple submissions during redirect
        this.showToast(
          "success",
          "OTP Sent",
          "A verification code has been sent to your email. Please check your inbox."
        );

        // Small delay to show toast before redirect
        setTimeout(() => {
          this.$router.push({
            name: "VerifyOTP",
            query: {
              email: this.form.email.trim(),
              remember: this.form.remember.toString(),
            },
          });
        }, 500);
      } catch (error) {
        // Handle network errors or other fetch failures
        if (error.name === "TypeError" || error.message.includes("fetch")) {
          this.handleNetworkError(error);
        } else {
          console.error("Login Error:", error);
          this.errors.email = "An unexpected error occurred. Please try again.";
          this.showToast(
            "error",
            "Login Error",
            "An unexpected error occurred. Please try again."
          );
        }
      } finally {
        // Only re-enable button if there was an error (not redirecting)
        // If redirecting, loading stays true to prevent double submission
        if (this.errors.email || this.errors.password || this.accountSuspended) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
/* Error text style */
.error-text {
  color: #e63946;
  font-size: 0.85rem;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.input-error {
  border-color: #e63946 !important;
}

/* Suspension Message */
.suspension-message {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  color: #856404;
}

.suspension-message i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #f39c12;
}

.suspension-message p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.suspension-message strong {
  color: #e74c3c;
}
/* ===== Page Background ===== */
.signin-page {
  width: 100%;
  min-height: 100vh;
  background: var(--background-color1);
  color: var(--text-color1);
  transition: background 0.3s ease;
  display: flex;
  flex-direction: column;
  padding-top: 90px;
}

/* ===== Center Wrapper ===== */
.signin-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 3rem 1rem;
}

/* ===== Main Card ===== */
.signin-card {
  width: 100%;
  max-width: 480px;
  background: var(--background-color2);
  padding: 2.5rem 2rem;
  border-radius: 14px;
  box-shadow: 0 0 10px var(--box-shadow);
  text-align: center;
  animation: fadeIn 0.6s ease;
}

/* ===== Title ===== */
.title {
  font-size: 1.8rem;
  margin-bottom: 1.6rem;
  font-weight: 600;
  color: var(--text-color1);
}

/* ===== Form Layout ===== */
.signin-form {
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
}

/* ===== Input Groups ===== */
.form-group {
  text-align: left;
}

label {
  font-size: 1rem;
  color: var(--text-color2);
  margin-bottom: 6px;
  display: block;
}

input {
  width: 100%;
  padding: 12px;
  font-size: 1rem;
  background: var(--background-color3);
  border: 1.5px solid var(--border-color-hover1);
  color: var(--text-color1);
  border-radius: 6px;
  outline: none;
  transition: 0.25s ease;
}

input:focus {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 8px var(--border-color-hover1);
}

/* ===== Remember Row ===== */
.remember-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

#remember {
  width: 16px;
  height: 16px;
}

.forgot-link {
  color: var(--border-color-hover);
  font-size: 0.9rem;
}

.forgot-link:hover {
  text-decoration: underline;
}

/* ===== Sign In Button ===== */
.submit-btn {
  width: 100%;
  padding: 12px;
  font-size: 1.15rem;
  font-weight: 600;
  background: var(--border-color-hover);
  border: none;
  border-radius: 8px;
  color: var(--text-color3);
  cursor: pointer;
  transition: 0.25s ease;
}

.submit-btn:hover {
  background: var(--text-color1);
  color: var(--text-color3);
  transform: scale(1.03);
}

/* ===== Create Account Footer ===== */
.create-account {
  margin-top: 1.4rem;
  font-size: 0.95rem;
  color: var(--text-color2);
}

.create-account a {
  color: var(--border-color-hover);
  font-weight: 500;
}

.create-account a:hover {
  text-decoration: underline;
}

/* ===== Animation ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Responsive Design ===== */

/* Tablet and Small Desktop (768px - 1024px) */
@media (max-width: 1024px) {
  .signin-wrapper {
    padding: 2.5rem 1.5rem;
  }

  .signin-card {
    max-width: 450px;
    padding: 2.25rem 1.75rem;
  }

  .title {
    font-size: 1.7rem;
    margin-bottom: 1.4rem;
  }
}

/* Tablet Portrait (481px - 768px) */
@media (max-width: 768px) {
  .signin-page {
    padding-top: 80px;
  }

  .signin-wrapper {
    padding: 2rem 1rem;
  }

  .signin-card {
    max-width: 420px;
    padding: 2rem 1.5rem;
  }

  .title {
    font-size: 1.6rem;
    margin-bottom: 1.3rem;
  }

  .signin-form {
    gap: 1.2rem;
  }

  label {
    font-size: 0.95rem;
  }

  input {
    padding: 11px;
    font-size: 0.98rem;
  }

  .submit-btn {
    padding: 11px;
    font-size: 1.1rem;
  }

  .remember-row {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .forgot-link {
    font-size: 0.85rem;
  }

  .create-account {
    font-size: 0.9rem;
  }
}

/* Mobile Landscape and Small Tablets (481px - 600px) */
@media (max-width: 600px) {
  .signin-card {
    max-width: 100%;
    padding: 1.75rem 1.25rem;
  }

  .title {
    font-size: 1.5rem;
    margin-bottom: 1.2rem;
  }

  .signin-form {
    gap: 1.1rem;
  }

  .error-text {
    font-size: 0.8rem;
  }

  .suspension-message {
    padding: 0.85rem;
  }

  .suspension-message i {
    font-size: 1.3rem;
  }

  .suspension-message p {
    font-size: 0.88rem;
  }
}

/* Mobile Portrait (320px - 480px) */
@media (max-width: 480px) {
  .signin-page {
    padding-top: 70px;
  }

  .signin-wrapper {
    padding: 1.5rem 0.75rem;
  }

  .signin-card {
    padding: 1.5rem 1rem;
    border-radius: 12px;
  }

  .title {
    font-size: 1.4rem;
    margin-bottom: 1.1rem;
  }

  .signin-form {
    gap: 1rem;
  }

  label {
    font-size: 0.9rem;
    margin-bottom: 5px;
  }

  input {
    padding: 10px;
    font-size: 0.95rem;
    border-radius: 5px;
  }

  .error-text {
    font-size: 0.75rem;
    margin-bottom: 3px;
  }

  .remember-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .checkbox-group {
    width: 100%;
  }

  .forgot-link {
    font-size: 0.85rem;
    width: 100%;
    text-align: right;
  }

  .submit-btn {
    padding: 11px;
    font-size: 1.05rem;
    border-radius: 6px;
  }

  .submit-btn:hover {
    transform: scale(1.01);
  }

  .create-account {
    margin-top: 1.2rem;
    font-size: 0.85rem;
    line-height: 1.5;
  }

  .suspension-message {
    padding: 0.75rem;
    border-radius: 6px;
  }

  .suspension-message i {
    font-size: 1.2rem;
    margin-bottom: 0.4rem;
  }

  .suspension-message p {
    font-size: 0.85rem;
    line-height: 1.3;
  }
}

/* Extra Small Mobile (320px - 360px) */
@media (max-width: 360px) {
  .signin-wrapper {
    padding: 1rem 0.5rem;
  }

  .signin-card {
    padding: 1.25rem 0.75rem;
  }

  .title {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }

  input {
    padding: 9px;
    font-size: 0.9rem;
  }

  .submit-btn {
    padding: 10px;
    font-size: 1rem;
  }

  .create-account {
    font-size: 0.8rem;
  }
}

/* Large Desktop (1440px+) */
@media (min-width: 1440px) {
  .signin-wrapper {
    padding: 4rem 1rem;
  }

  .signin-card {
    max-width: 520px;
    padding: 3rem 2.5rem;
  }

  .title {
    font-size: 2rem;
    margin-bottom: 2rem;
  }

  .signin-form {
    gap: 1.6rem;
  }

  input {
    padding: 14px;
    font-size: 1.05rem;
  }

  .submit-btn {
    padding: 14px;
    font-size: 1.2rem;
  }
}

/* Landscape Orientation Adjustments */
@media (max-height: 600px) and (orientation: landscape) {
  .signin-page {
    padding-top: 60px;
  }

  .signin-wrapper {
    padding: 1rem;
  }

  .signin-card {
    padding: 1.5rem 2rem;
  }

  .title {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }

  .signin-form {
    gap: 0.9rem;
  }
}
</style>